import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, Code as CodeIcon, Copy, Eye, Maximize2, X } from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";

/* ────────────────── Phase 7a — sandboxed artifact preview ──────────────────
 * Renders an HTML or SVG fenced code block from Claude with three views:
 *
 *   Code     ─ syntax-highlighted source (same look as our normal CodeBlock)
 *   Preview  ─ rendered in a SANDBOXED <iframe srcdoc=…>
 *              For html: sandbox="allow-scripts" (opaque origin, scripts run
 *                        but cannot reach parent window).
 *              For svg:  sandbox=""             (no scripts whatsoever).
 *   Expand   ─ moves the active view into a right-edge overlay panel
 *              (~50% viewport, Esc / outside-click to close).
 *
 * Copy always copies the raw source.
 * ────────────────────────────────────────────────────────────────────────── */

// Iframe sizing constants.
const MIN_H = 120;
const DEFAULT_H = 360;
const maxIframeHeight = () =>
  typeof window === "undefined" ? 800 : Math.round(window.innerHeight * 0.8);

function svgSrcdoc(code) {
  // Wrap a bare SVG node in minimal HTML so the iframe can lay it out
  // centered against a clean white background. NO scripts allowed by the
  // sandbox attr on the iframe itself.
  return `<!doctype html>
<html><head><meta charset="utf-8"><style>
  html,body{margin:0;height:100%;background:#ffffff;}
  body{display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;}
  svg{max-width:100%;max-height:100%;height:auto;}
</style></head><body>${code}</body></html>`;
}

function htmlSrcdoc(code) {
  // Claude's fenced html is already a full or near-full HTML document most of
  // the time; if it's a fragment, wrap it so it still renders cleanly.
  const looksFullDoc = /<html[\s>]/i.test(code) || /<!doctype/i.test(code);
  if (looksFullDoc) return code;
  return `<!doctype html>
<html><head><meta charset="utf-8"><style>
  html,body{margin:0;background:#ffffff;font-family:ui-sans-serif,system-ui;}
  body{padding:16px;}
</style></head><body>${code}</body></html>`;
}

/* ─────────────── Tab / control button ─────────────── */
function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-sans font-medium transition-colors ${
        active
          ? "bg-[#2C2A29] text-white"
          : "text-[#2C2A29] hover:bg-[#E8E2D2]"
      }`}
    >
      <Icon size={11} strokeWidth={2} />
      <span>{label}</span>
    </button>
  );
}

/* ─────────────── Copy with feedback ─────────────── */
function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        /* ignore */
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-sans font-medium text-[#2C2A29] hover:bg-[#E8E2D2] transition-colors"
    >
      {copied ? (
        <>
          <Check size={11} strokeWidth={2} /> Copied
        </>
      ) : (
        <>
          <Copy size={11} strokeWidth={2} /> Copy
        </>
      )}
    </button>
  );
}

/* ─────────────── Code view (syntax-highlighted) ─────────────── */
function ArtifactCodeView({ language, code, testid }) {
  return (
    <div
      className="overflow-x-auto bg-[#FBF8F2]"
      data-testid={testid}
    >
      <SyntaxHighlighter
        language={language === "svg" ? "xml" : language}
        style={oneLight}
        customStyle={{
          margin: 0,
          padding: "0.75rem 1rem",
          background: "transparent",
          fontSize: "12.5px",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          lineHeight: 1.55,
        }}
        codeTagProps={{
          style: {
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          },
        }}
        PreTag="div"
        wrapLongLines={false}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/* ─────────────── Preview view (sandboxed iframe) ─────────────── */
function ArtifactPreviewView({
  language,
  code,
  height,
  resizable,
  onHeightChange,
  testid,
}) {
  const srcdoc = useMemo(
    () => (language === "svg" ? svgSrcdoc(code) : htmlSrcdoc(code)),
    [language, code],
  );
  // svg → empty sandbox (no scripts at all). html → allow scripts + modals
  // (so alert/confirm/prompt demos work) but NOT allow-same-origin, so the
  // iframe stays in an opaque origin and cannot reach parent.document /
  // top.window.
  const sandbox = language === "svg" ? "" : "allow-scripts allow-modals";

  // Drag-to-resize state
  const dragRef = useRef(null);
  const startY = useRef(0);
  const startH = useRef(0);
  const onPointerDown = (e) => {
    if (!resizable) return;
    e.preventDefault();
    startY.current = e.clientY;
    startH.current = height;
    dragRef.current?.setPointerCapture(e.pointerId);
    const onMove = (ev) => {
      const dy = ev.clientY - startY.current;
      const next = Math.min(
        maxIframeHeight(),
        Math.max(MIN_H, startH.current + dy),
      );
      onHeightChange(next);
    };
    const onUp = (ev) => {
      try {
        dragRef.current?.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      className="relative bg-white"
      style={{ height: resizable ? `${height}px` : "100%" }}
      data-testid={testid}
    >
      <iframe
        title={`artifact-preview-${language}`}
        srcDoc={srcdoc}
        sandbox={sandbox}
        referrerPolicy="no-referrer"
        loading="lazy"
        className="block w-full h-full border-0 bg-white"
        data-testid={`${testid}-iframe`}
        data-sandbox={sandbox || "empty"}
      />
      {resizable ? (
        <div
          ref={dragRef}
          onPointerDown={onPointerDown}
          className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize flex items-center justify-center select-none group"
          data-testid={`${testid}-resize-handle`}
          role="separator"
          aria-orientation="horizontal"
          tabIndex={-1}
        >
          <span className="block w-10 h-[3px] rounded-full bg-[#DCD7CE] group-hover:bg-[#7A4A2C] transition-colors" />
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────── Expand overlay panel ─────────────── */
function ArtifactExpandPanel({ language, code, onClose, messageId }) {
  const [mode, setMode] = useState("preview");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const node = (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      data-testid={`artifact-expand-panel-${messageId}`}
      onMouseDown={(e) => {
        // Click on the backdrop (not the panel itself) closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0E0D0C]/45"
        data-testid={`artifact-expand-backdrop-${messageId}`}
        onMouseDown={onClose}
      />
      {/* Panel */}
      <aside
        className="relative z-[61] h-full w-full sm:w-1/2 max-w-[1100px] bg-[#FAF9F7] border-l border-[#DCD7CE] shadow-2xl flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#DCD7CE] bg-[#F3EFE5]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold">
              Artifact · {language}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <TabButton
              active={mode === "code"}
              onClick={() => setMode("code")}
              icon={CodeIcon}
              label="Code"
              testid={`artifact-expand-tab-code-${messageId}`}
            />
            <TabButton
              active={mode === "preview"}
              onClick={() => setMode("preview")}
              icon={Eye}
              label="Preview"
              testid={`artifact-expand-tab-preview-${messageId}`}
            />
            <CopyButton
              value={code}
              testid={`artifact-expand-copy-${messageId}`}
            />
            <button
              type="button"
              onClick={onClose}
              data-testid={`artifact-expand-close-${messageId}`}
              aria-label="Close"
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-sans font-medium text-[#2C2A29] hover:bg-[#E8E2D2] transition-colors"
            >
              <X size={11} strokeWidth={2.2} />
            </button>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden">
          {mode === "code" ? (
            <div className="h-full overflow-auto">
              <ArtifactCodeView
                language={language}
                code={code}
                testid={`artifact-expand-code-view-${messageId}`}
              />
            </div>
          ) : (
            <ArtifactPreviewView
              language={language}
              code={code}
              height={0}
              resizable={false}
              onHeightChange={() => {}}
              testid={`artifact-expand-preview-${messageId}`}
            />
          )}
        </div>
      </aside>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}

/* ─────────────── Main artifact block ─────────────── */
export default function ArtifactBlock({ language, code, messageId }) {
  const [mode, setMode] = useState("code");
  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState(DEFAULT_H);

  const closeExpand = useCallback(() => setExpanded(false), []);

  return (
    <div
      className="not-prose my-3 rounded-lg overflow-hidden border border-[#DCD7CE] bg-[#FBF8F2]"
      data-testid={`artifact-block-${messageId}`}
      data-language={language}
      data-mode={mode}
    >
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-[#DCD7CE] bg-[#F3EFE5] gap-2">
        <span className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold truncate">
          {language}
        </span>
        <div className="flex items-center gap-1">
          <TabButton
            active={mode === "code"}
            onClick={() => setMode("code")}
            icon={CodeIcon}
            label="Code"
            testid={`artifact-tab-code-${messageId}`}
          />
          <TabButton
            active={mode === "preview"}
            onClick={() => setMode("preview")}
            icon={Eye}
            label="Preview"
            testid={`artifact-tab-preview-${messageId}`}
          />
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid={`artifact-tab-expand-${messageId}`}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-sans font-medium text-[#2C2A29] hover:bg-[#E8E2D2] transition-colors"
          >
            <Maximize2 size={11} strokeWidth={2} />
            <span>Expand</span>
          </button>
          <CopyButton value={code} testid={`artifact-copy-${messageId}`} />
        </div>
      </div>

      {mode === "code" ? (
        <ArtifactCodeView
          language={language}
          code={code}
          testid={`artifact-code-view-${messageId}`}
        />
      ) : (
        <ArtifactPreviewView
          language={language}
          code={code}
          height={height}
          resizable={true}
          onHeightChange={setHeight}
          testid={`artifact-preview-view-${messageId}`}
        />
      )}

      {expanded ? (
        <ArtifactExpandPanel
          language={language}
          code={code}
          onClose={closeExpand}
          messageId={messageId}
        />
      ) : null}
    </div>
  );
}
